import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { hasPermission } from '@/constants/permissions';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { holidaysApi } from '@/features/institute/holidays/api/holidays.api';
import { HolidayFormModal } from '@/features/institute/holidays/components/HolidayFormModal';
import { describeDays, describeParentSms, formatHolidayRange } from '@/features/institute/holidays/utils';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { calendarApi } from '../api/calendar.api';
import { AcademicCalendarView, type CalendarManagement } from '../components/AcademicCalendarView';
import { EventFormModal, type HolidayDraft } from '../components/EventFormModal';
import { WorkingDaysSummary } from '../components/WorkingDaysSummary';
import type { EventPayload } from '../schemas/event.schema';
import type { CalendarEventCategory, CalendarEventItem } from '../types/calendar';
import type { Holiday, ParentSmsOutcome } from '@/types/holiday';

/**
 * The office's academic calendar: the shared `AcademicCalendarView`, plus
 * everything that changes it — the entry form, the removal confirmation and the
 * API calls behind both. The parent panel draws the same view without these.
 *
 * Holidays are declared, corrected and withdrawn here too, through the holiday
 * form and the holidays endpoint: they stay rows in `holidays`, the table the
 * attendance register and timetable read, rather than becoming calendar events.
 */
export function AcademicCalendarPage() {
  /**
   * `?month=2026-10` opens on that month — how the students roster's working
   * days tile shows the office the holidays behind its count. Anything else in
   * the address is ignored and the calendar opens on today.
   */
  const [searchParams] = useSearchParams();
  const month = searchParams.get('month');
  const initialDate = month && /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? `${month}-01` : undefined;

  /** The session being read — `''` is the current one, which is where every write goes. */
  const [academicYearUuid, setAcademicYearUuid] = useState('');

  /** The whole session, every entry and every audience — the office sees it all. */
  const { data, isLoading, error, refetch } = useApiQuery(
    () => calendarApi.list({ academicYearUuid: academicYearUuid || undefined }),
    [academicYearUuid],
  );
  /**
   * A past session is read-only here. The server plans every entry against the
   * current year, so an add or edit made while looking at last year would land
   * somewhere other than where the office is looking.
   */
  const isPastYear = Boolean(data && !data.academicYear.isCurrent);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEventItem | null>(null);
  const [formDefaults, setFormDefaults] = useState<{
    date?: string;
    category?: CalendarEventCategory;
  }>({});

  /** Held rather than deleted on the spot — the confirmation names what goes. */
  const [removing, setRemoving] = useState<CalendarEventItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /** Holidays carry their own key; without it they stay read-only here. */
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManageCalendar = hasPermission(permissions, 'CALENDAR_MANAGE');
  const canManageHolidays = hasPermission(permissions, 'HOLIDAY_MANAGE');
  const [isHolidayFormOpen, setIsHolidayFormOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [holidayDraft, setHolidayDraft] = useState<HolidayDraft | undefined>();
  /**
   * The saved event being turned into a holiday. Removed only once the holiday
   * form has saved, so closing that form leaves the event exactly as it was.
   */
  const [convertingEvent, setConvertingEvent] = useState<CalendarEventItem | null>(null);

  function openHolidayForm(holiday: Holiday | null, draft?: HolidayDraft) {
    setEditingHoliday(holiday);
    setHolidayDraft(draft);
    setIsHolidayFormOpen(true);
  }

  /** "Holiday" picked as an event's category: the holiday form takes over. */
  function switchToHolidayForm(draft: HolidayDraft) {
    setIsFormOpen(false);
    setConvertingEvent(editingEvent);
    openHolidayForm(null, draft);
  }

  function closeHolidayForm() {
    setIsHolidayFormOpen(false);
    setConvertingEvent(null);
  }

  /**
   * Takes the old event off the calendar once its holiday exists.
   *
   * Two tables, two endpoints, so not one transaction — and ordered so the
   * failure is the harmless one: the holiday is declared first, and if the
   * event then cannot be removed it shows twice, with a message saying which to
   * remove, rather than the day vanishing from the calendar altogether.
   */
  async function retireConvertedEvent(event: CalendarEventItem) {
    try {
      await calendarApi.remove(event.uuid);
    } catch (caught) {
      toast.error(
        `${event.title} is still on the calendar as an event`,
        caught instanceof ApiRequestError
          ? `${caught.message} Remove the event to leave only the holiday.`
          : 'Remove the event to leave only the holiday.',
      );
    }
  }

  /**
   * The calendar holds only what it draws; the form needs the whole record
   * (who is working, the type), so it is read from the holidays list first.
   */
  async function editHoliday(item: CalendarEventItem) {
    try {
      const holiday = (await holidaysApi.list()).find((entry) => entry.uuid === item.uuid);
      if (!holiday) {
        toast.error('That holiday is no longer there', 'The calendar has been reloaded.');
        refetch();
        return;
      }
      openHolidayForm(holiday);
    } catch (caught) {
      toast.error(
        `Could not open ${item.title}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    }
  }

  async function handleHolidaySaved(saved: Holiday, wasCreated: boolean, parentSms: ParentSmsOutcome | null) {
    const converted = convertingEvent;
    closeHolidayForm();
    setEditingHoliday(null);
    if (converted && wasCreated) await retireConvertedEvent(converted);

    toast.success(
      wasCreated ? `${saved.name} is declared` : `${saved.name} has been updated`,
      `${formatHolidayRange(saved)} · ${describeDays(saved.days)}`,
    );

    // Its own toast, because it can fail while the declaration did not: the
    // office must never read "declared" and assume the parents were told.
    if (parentSms?.error) {
      toast.error('Parents were not sent the SMS', parentSms.error);
    } else if (parentSms) {
      const notify = parentSms.failed > 0 ? toast.info : toast.success;
      notify('SMS sent to parents', describeParentSms(parentSms));
    }
    refetch();
  }

  function openEditor(item: CalendarEventItem | null, defaults: typeof formDefaults = {}) {
    setEditingEvent(item);
    setFormDefaults(defaults);
    setIsFormOpen(true);
  }

  // View only — or a past session — draws the calendar with no add, edit or
  // remove, the same view a parent gets. The API refuses the writes either way; this keeps the buttons
  // from offering what will be refused.
  const manage: CalendarManagement | undefined = canManageCalendar && !isPastYear
    ? {
        onAdd: (defaults) => openEditor(null, defaults),
        onEdit: (item) => (item.source === 'HOLIDAY' ? void editHoliday(item) : openEditor(item)),
        onRemove: setRemoving,
        ...(canManageHolidays && { onAddHoliday: () => openHolidayForm(null) }),
      }
    : undefined;

  const isRemovingHoliday = removing?.source === 'HOLIDAY';

  /**
   * Saves through the API and reloads the session.
   *
   * Rejects rather than reporting the failure itself: the modal is still open
   * over an unsaved entry, and that is where the reason belongs. A refused save
   * must not close the form and lose what was typed.
   */
  async function handleSaveEvent(payload: EventPayload) {
    try {
      if (editingEvent) {
        await calendarApi.update(editingEvent.uuid, payload);
        toast.success(
          `${payload.title} has been updated`,
          'The academic calendar now shows the change.',
        );
      } else {
        await calendarApi.create(payload);
        toast.success(
          `${payload.title} is on the calendar`,
          'It now appears against its date for everyone.',
        );
      }
      refetch();
    } catch (caught) {
      throw caught instanceof ApiRequestError
        ? new Error(caught.message)
        : new Error('Something went wrong. Please try again.');
    }
  }

  async function confirmRemove() {
    if (!removing) return;

    setIsDeleting(true);
    try {
      if (isRemovingHoliday) {
        await holidaysApi.remove(removing.uuid);
        toast.success(
          `${removing.title} has been withdrawn`,
          'Those days count as working days again.',
        );
      } else {
        await calendarApi.remove(removing.uuid);
        toast.success(
          `${removing.title} has been removed`,
          'It no longer appears on the academic calendar.',
        );
      }
      setRemoving(null);
      refetch();
    } catch (caught) {
      toast.error(
        'Could not remove that entry',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <>
      <AcademicCalendarView
        data={data}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        title="Academic calendar"
        description="View and manage all academic events, holidays and important dates for your school."
        emptyDescription="Add the exam weeks, meetings, holidays and important dates of this session and they will appear here for everyone at your school."
        className={CONSOLE_PALETTE}
        manage={manage}
        initialDate={initialDate}
        hideCategoryTiles
        summary={
          <WorkingDaysSummary
            data={data}
            isLoading={isLoading}
            academicYearUuid={academicYearUuid}
            onAcademicYearChange={setAcademicYearUuid}
          />
        }
      />

      <EventFormModal
        eventItem={editingEvent}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSaveEvent}
        defaultDate={formDefaults.date}
        defaultCategory={formDefaults.category}
        onChooseHoliday={canManageHolidays ? switchToHolidayForm : undefined}
      />

      <HolidayFormModal
        holiday={editingHoliday}
        draft={holidayDraft}
        isOpen={isHolidayFormOpen}
        onClose={closeHolidayForm}
        onSaved={(saved, wasCreated, parentSms) => void handleHolidaySaved(saved, wasCreated, parentSms)}
      />

      <ConfirmDialog
        isOpen={Boolean(removing)}
        tone="danger"
        title={`${isRemovingHoliday ? 'Withdraw' : 'Remove'} ${removing?.title ?? 'this entry'}?`}
        description={
          isRemovingHoliday
            ? 'Those days go back to being working days, so anyone not marked present on them will count as absent.'
            : 'It stops appearing on the academic calendar for everyone at your school.'
        }
        confirmLabel={isRemovingHoliday ? 'Withdraw holiday' : 'Remove it'}
        isLoading={isDeleting}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
